import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  CheckCircle2, XCircle, AlertCircle, Database, Cpu,
  CreditCard, Mail, RefreshCw, Settings, ToggleLeft, ToggleRight, Save, Loader2,
  Copy, KeyRound, Link2, Eye, EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_API_URL}/api`;
const FRONTEND_URL = process.env.REACT_APP_API_URL?.replace("/api", "") || window.location.origin;

function StatusBadge({ status }) {
  if (status === "ok") return <span className="flex items-center gap-1.5 text-xs font-mono text-verdict-real"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>;
  if (status === "error") return <span className="flex items-center gap-1.5 text-xs font-mono text-verdict-fake"><XCircle className="w-3.5 h-3.5" /> Error</span>;
  return <span className="flex items-center gap-1.5 text-xs font-mono text-verdict-uncertain"><AlertCircle className="w-3.5 h-3.5" /> {status}</span>;
}

function HealthCard({ icon: Icon, label, data }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">{label}</span>
        </div>
        <StatusBadge status={data?.status || "unknown"} />
      </div>
      {data && Object.entries(data).filter(([k]) => k !== "status").map(([k, v]) => (
        <div key={k} className="flex justify-between text-xs font-mono">
          <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
          <span className="text-foreground">{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminSystem() {
  const { t } = useTranslation();
  const [health, setHealth]   = useState(null);
  const [config, setConfig]   = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [maintenanceWhitelistIps, setMaintenanceWhitelistIps] = useState("");
  const [savingMsg, setSavingMsg] = useState(false);
  const [savingWhitelist, setSavingWhitelist] = useState(false);
  const [dbStats, setDbStats] = useState(null);
  const [bypassToken, setBypassToken] = useState(null);
  const [showBypassToken, setShowBypassToken] = useState(false);
  const [adminIp, setAdminIp] = useState("unknown");

  const loadHealth = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/system/health`, { withCredentials: true });
      setHealth(res.data);
    } catch { toast.error(t("admin.system.healthFail")); } finally { setRefreshing(false); }
  }, [t]);

  const loadBypassToken = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/system/bypass-token`, { withCredentials: true });
      setBypassToken(res.data.bypass_token);
    } catch { }
  }, []);

  const loadAdminIp = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/my-ip`, { withCredentials: true });
      setAdminIp(res?.data?.ip || "unknown");
    } catch {
      setAdminIp("unknown");
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/system/config`, { withCredentials: true });
      setConfig(res.data);
      setMaintenanceMsg(res.data.maintenance_message || "");
      setMaintenanceWhitelistIps(res.data.maintenance_whitelist_ips || "");
      // Load bypass token if maintenance is on
      if (res.data.maintenance_mode) {
        loadBypassToken();
      } else {
        setBypassToken(null);
      }
    } catch { }
  }, [loadBypassToken]);

  useEffect(() => {
    loadHealth(); loadConfig();
    loadAdminIp();
    axios.get(`${API}/admin/db-stats`, { withCredentials: true }).then(r => setDbStats(r.data)).catch(() => {});
  }, [loadAdminIp, loadConfig, loadHealth]);

  const toggleConfig = async (key) => {
    if (!config) return;
    const updated = { ...config, [key]: !config[key] };
    setConfig(updated);
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/system/config`, { [key]: updated[key] }, { withCredentials: true });
      toast.success(t("admin.system.configUpdated"));
      // Capture bypass token when enabling maintenance mode
      if (key === "maintenance_mode" && updated[key] && res.data.bypass_token) {
        setBypassToken(res.data.bypass_token);
      } else if (key === "maintenance_mode" && !updated[key]) {
        setBypassToken(null);
      }
      await loadConfig();
    } catch { toast.error(t("admin.system.configFail")); setConfig(config); } finally { setSaving(false); }
  };

  const saveMaintenanceMsg = async () => {
    setSavingMsg(true);
    try {
      await axios.patch(`${API}/admin/system/config`, { maintenance_message: maintenanceMsg }, { withCredentials: true });
      toast.success(t("admin.system.maintenanceMsgSaved"));
    } catch { toast.error(t("admin.system.configFail")); } finally { setSavingMsg(false); }
  };

  const saveMaintenanceWhitelistIps = async () => {
    setSavingWhitelist(true);
    try {
      await axios.patch(
        `${API}/admin/system/config`,
        { maintenance_whitelist_ips: maintenanceWhitelistIps },
        { withCredentials: true },
      );
      toast.success(t("admin.system.configUpdated"));
      await loadConfig();
    } catch {
      toast.error(t("admin.system.configFail"));
    } finally {
      setSavingWhitelist(false);
    }
  };

  const addCurrentIpToWhitelist = () => {
    const ip = String(adminIp || "").trim();
    if (!ip || ip === "unknown") return;
    const current = String(maintenanceWhitelistIps || "");
    const lines = current
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.includes(ip)) return;
    const merged = [...lines, ip].join("\n");
    setMaintenanceWhitelistIps(merged);
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-system-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold">{t("admin.system.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("admin.system.subtitle")}</p>
      </div>

      {/* Health Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">{t("admin.system.health")}</h2>
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs h-7" onClick={loadHealth} disabled={refreshing} data-testid="refresh-health-btn">
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            {t("admin.system.refresh")}
          </Button>
        </div>
        {health ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <HealthCard icon={Database} label="PostgreSQL"  data={health.postgresql}  />
            <HealthCard icon={Cpu}      label="Gemini AI" data={health.gemini}  />
            <HealthCard icon={CreditCard} label="Midtrans" data={health.midtrans}  />
            <HealthCard icon={Mail}     label="Email"     data={health.email}   />
            <HealthCard icon={Settings} label="yt-dlp"   data={health.yt_dlp}  />
            <div className="rounded-md border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t("admin.system.dbStats")}</span>
              </div>
              {health.database_stats && Object.entries(health.database_stats).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs font-mono mb-1">
                  <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="font-semibold">{v.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="h-28 rounded-md border border-border bg-card animate-pulse" />)}
          </div>
        )}
      </div>

      {/* Config Section */}
      <div className="mb-8">
        <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("admin.system.config")}</h2>
        {config ? (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-card overflow-hidden">
              {[
                { key: "maintenance_mode",    label: t("admin.system.maintenance"),  desc: t("admin.system.maintenanceDesc") },
                { key: "registration_enabled", label: t("admin.system.registration"), desc: t("admin.system.registrationDesc") },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between p-5 border-b border-border last:border-0">
                  <div>
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                  </div>
                  <button
                    onClick={() => toggleConfig(key)}
                    disabled={saving}
                    className="text-brand-primary"
                    data-testid={`toggle-${key}`}
                  >
                    {config[key] ? (
                      <ToggleRight className="w-8 h-8 text-verdict-real" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-muted-foreground" />
                    )}
                  </button>
                </div>
              ))}
            </div>

            {/* Maintenance Message Input */}
            <div className="rounded-md border border-border bg-card p-5" data-testid="maintenance-message-section">
              <div className="mb-3">
                <div className="text-sm font-medium">{t("admin.system.maintenanceMsg")}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t("admin.system.maintenanceMsgDesc")}</div>
              </div>
              <textarea
                value={maintenanceMsg}
                onChange={e => setMaintenanceMsg(e.target.value)}
                placeholder={t("admin.system.maintenanceMsgPlaceholder")}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono resize-none h-24 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                data-testid="maintenance-message-input"
              />
              <div className="flex justify-end mt-3">
                <Button size="sm" className="rounded-sm bg-brand-primary text-white gap-1.5" onClick={saveMaintenanceMsg} disabled={savingMsg} data-testid="save-maintenance-msg-btn">
                  {savingMsg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {t("common.save")}
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-border bg-card p-5" data-testid="maintenance-whitelist-section">
              <div className="mb-3">
                <div className="text-sm font-medium">IP Whitelist</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  IP yang bisa akses penuh saat maintenance. Satu IP per baris.
                  IP Anda saat ini ditambahkan otomatis saat maintenance diaktifkan.
                </div>
              </div>
              <textarea
                value={maintenanceWhitelistIps}
                onChange={(e) => setMaintenanceWhitelistIps(e.target.value)}
                placeholder={"103.x.x.x\n116.x.x.x"}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono resize-none h-24 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                data-testid="maintenance-whitelist-input"
                spellCheck={false}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>
                  IP Anda saat ini: <code className="px-1 py-0.5 rounded bg-secondary/40 text-foreground">{adminIp || "unknown"}</code>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm h-7 text-xs"
                  onClick={addCurrentIpToWhitelist}
                  data-testid="maintenance-whitelist-add-current-ip"
                >
                  + Tambah
                </Button>
              </div>
              <div className="flex justify-end mt-3">
                <Button
                  size="sm"
                  className="rounded-sm bg-brand-primary text-white gap-1.5"
                  onClick={saveMaintenanceWhitelistIps}
                  disabled={savingWhitelist}
                  data-testid="save-maintenance-whitelist-btn"
                >
                  {savingWhitelist ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {t("common.save")}
                </Button>
              </div>
            </div>

            {/* Bypass URL — only shown when maintenance mode is ON */}
            {config?.maintenance_mode && bypassToken && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-5" data-testid="bypass-url-section">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound className="w-4 h-4 text-amber-500" />
                  <div className="text-sm font-medium text-amber-500">Bypass URL (Admin Only)</div>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Gunakan URL ini untuk mengakses website selama maintenance. Jangan bagikan ke publik.
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-background border border-border rounded-sm px-3 py-2 text-xs font-mono overflow-hidden text-ellipsis whitespace-nowrap" data-testid="bypass-url-display">
                    {showBypassToken
                      ? `${window.location.origin}/maintenance?key=${bypassToken}`
                      : `${window.location.origin}/maintenance?key=${"*".repeat(20)}`
                    }
                  </div>
                  <Button
                    size="sm" variant="outline" className="rounded-sm gap-1 text-xs h-8 shrink-0"
                    onClick={() => setShowBypassToken(!showBypassToken)}
                    data-testid="toggle-bypass-visibility"
                  >
                    {showBypassToken ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </Button>
                  <Button
                    size="sm" className="rounded-sm bg-amber-500 text-white gap-1 text-xs h-8 shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/maintenance?key=${bypassToken}`);
                      toast.success("Bypass URL disalin!");
                    }}
                    data-testid="copy-bypass-url-btn"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="h-32 rounded-md border border-border bg-card animate-pulse" />
        )}
      </div>

      {/* DB Stats Section */}
      {dbStats && (
        <div className="mt-8" data-testid="db-stats-section">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("admin.system.dbStats")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            {Object.entries(dbStats.collections || {}).map(([k, v]) => (
              <div key={k} className="rounded-md border border-border bg-card p-4 text-center">
                <div className="font-heading text-xl font-bold">{v.toLocaleString()}</div>
                <div className="text-xs font-mono text-muted-foreground capitalize">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-md border border-border bg-card p-4">
              <div className="text-xs font-mono text-muted-foreground uppercase tracking-widest mb-2">{t("admin.system.planDist")}</div>
              {Object.entries(dbStats.plan_distribution || {}).map(([plan, count]) => (
                <div key={plan} className="flex justify-between text-sm mb-1">
                  <span className="capitalize font-mono">{plan}</span>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
            <div className="rounded-md border border-border bg-card p-4">
              <div className="text-xs font-mono text-muted-foreground uppercase tracking-widest mb-2">{t("admin.system.activeUsers")}</div>
              <div className="font-heading text-3xl font-bold">{dbStats.active_users_7d || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">{t("admin.system.last7days")}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
